<template>
  <div
    :id="`ishi-cause-${cause.id}`"
    class="ishikawa-cause"
    :class="causeClasses"
    :data-id="cause.id"
    :style="itemStyle"
  >
    <canvas ref="canvas" />
    <div
      ref="wrapper"
      class="ishikawa-cause__wrapper"
      :style="wrapperStyle"
      @click="$refs.label.doFocus()"
    >
      <DraggingIndication />
      <EditableLabel
        ref="label"
        v-model="cause.text"
        class="ishikawa-cause__wrapper__text generic-border--focus-shadow"
        :class="{ 'ishikawa-cause__wrapper__text--editing': editing }"
        :disabled="disabled"
        :placeholder="$t('message.CLICK_TO_TYPE')"
        use-textarea
        @blur="editing = false"
        @input="editCause({ text: $event })"
        @keyup="textSizeChanged"
        @startEdit="editing = true"
        @textareaAutoSize="textSizeChanged"
      />
      <CauseVerificationCheckbox :cause="cause" />
      <div class="ishikawa-cause__sep" />
      <ActionsPerCauseBtn :cause="cause" :problem-id="problemId" />

      <div v-if="sheetSelection" class="ishikawa-cause__wrapper__overlay" @click="showInSheet = !showInSheet">
        <Checkbox v-model="showInSheet" class="ishikawa-cause__wrapper__overlay__checkbox" round />
      </div>
    </div>
    <IshikawaCause
      v-for="(childCause, index) in cause.wCauses"
      :ref="`child${childCause.id}`"
      :key="childCause.id"
      :problem-id="problemId"
      :cause="childCause"
      :container="container"
      :index-from-center="(isBottom ? index : childCause.wCauses.length - index) + 1"
      :branch-element="branchElement"
      :disabled="disabled"
      is-child-cause
      :sheet-selection="sheetSelection"
      @size-update="textSizeChanged"
      @showActionsPerCauseModal="$emit('showActionsPerCauseModal', $event)"
    />
  </div>
</template>

<script>
/**
 * Item of an Ishikawa diagram
 *
 * TODO: Lint
 * TODO: TU
 *
 * @vue-prop {Number} problemId - Used for XHR
 * @vue-prop {Object} cause - Cause object
 * @vue-prop {HTMLElement} container - The element that this cause can be dragged in
 * @vue-prop {HTMLElement} branchElement - The branch element that this cause is attached to
 * @vue-prop {Boolean} [isChildCause] - Wether this cause is a child cause or a branch cause
 * @vue-prop {Boolean} [isBottom] - Wether this cause is in the bottom section or the top
 * @vue-prop {Boolean} [disabled] - Cannot be edited
 * @vue-prop {Boolean} [sheetSelection] - Wether the sheet selection is on or off
 *
 * @vue-event {Number} margin-set - Emits the newly set margin of the cause item
 * @vue-event {} size-update - Emitted when the size of the cause item was updated
 */

import $ from 'jquery';
// Vuex
import { ADD_CAUSE, EDIT_CAUSE } from '@/store/modules/problem/action-types';
// Components
import ActionsPerCauseBtn from '@/components/problem-sheet/actions-per-cause-btn.vue';
import CauseVerificationCheckbox from '@/components/cause-verification-checkbox.vue';
import Checkbox from '@/components/checkbox.vue';
import DraggingIndication from '@/components/dragging-indication.vue';
import EditableLabel from '@/components/editable-label.vue';
// Utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import {
  causeMargin,
  causeRightMargin,
  causeLeftMargin,
  branchAngleTan,
  getIshikawaCauseWidth,
} from '@/utils/ishikawa-utils';
import { calert, preventPastingStyles } from '@/utils/main';
import { SColors } from '@/utils/constants';

export default {

  name: 'IshikawaCause',

  components: {
    ActionsPerCauseBtn,
    CauseVerificationCheckbox,
    Checkbox,
    DraggingIndication,
    EditableLabel,
  },

  mixins: [mixinSocketHandler],

  props: {
    problemId: {
      type: Number,
      required: true,
    },
    cause: {
      type: Object,
      required: true,
    },
    container: {
      type: HTMLElement,
      default: null,
    },
    branchElement: {
      type: HTMLElement,
      default: undefined,
    },
    isChildCause: Boolean,
    isBottom: Boolean,
    disabled: Boolean,
    sheetSelection: Boolean,
  },

  data() {
    return {
      marginRight: 0,
      editing: false,
    };
  },

  computed: {
    /** Style applied to $el */
    itemStyle() {
      const width = getIshikawaCauseWidth(this.$store.state);
      return {
        width: this.noMargin ? 'calc(100% - 5px)' : `${width}px`,
        marginRight: this.noMargin ? `${causeMargin}px` : `${this.marginRight + causeRightMargin}px`,
        marginLeft: this.noMargin ? '0px' : `${-this.marginRight + causeLeftMargin}px`,
      };
    },
    noMargin() {
      return this.isChildCause || this.branchElement === undefined;
    },
    /** Style applied to the cause-wrapper element */
    wrapperStyle() {
      return {
        marginBottom: `${causeMargin}px`,
      };
    },
    causeClasses() {
      return {
        'in-sheet-selection': this.sheetSelection,
      };
    },
    showInSheet: {
      get() {
        return this.cause.hideInSheet !== 1;
      },
      set(val) {
        this.editCause({ hideInSheet: val ? 0 : 1 });
      },
    },
    /** Object: Cause's problem object */
    stateProblem() {
      return this.$store.state.problem.problems[`${this.problemId}`];
    },
    /** Number: Number of actions linked to this.cause */
    nActionsPerCause() {
      if (this.stateProblem.actions) {
        return this.stateProblem.actions.filter(a => a.cause === this.cause.id).length;
      }
      return 0;
    },
    isCauseFalse() {
      return this.cause.isReal === 0 && this.cause.isVerified;
    },
  },

  watch: {
    branchElement() {
      this.calculateMargin();
    },
    container() {
      this.setupDraggable();
    },
    sheetSelection(newVal) {
      $(this.$el).draggable(newVal ? 'disable' : 'enable');
    },
    editing() {
      // Deactivate drag when editing the text
      if (this.editing) {
        $(this.$el).draggable('disable');
      } else {
        $(this.$el).draggable('enable');
      }
    },
  },

  mounted() {
    this.init();
  },

  updated() {
    // Branch element may be shifted upon mount
    this.calculateMargin();
    this.drawLinks();
    this.hasBeenUpdated = true;
  },

  beforeDestroy() {
    this.destroyDraggableDroppable();
  },

  methods: {
    init() {
      this.destroyDraggableDroppable();
      this.setupDraggable();
      this.setupDroppable();
      this.calculateMargin();
      this.drawLinks();
    },
    destroyDraggableDroppable() {
      let inst = $(this.$el).draggable('instance');
      if (inst) {
        $(this.$el).draggable('destroy');
      }
      inst = $(this.$el).droppable('instance');
      if (inst) {
        $(this.$el).droppable('destroy');
      }
    },
    /** Reset the right margin of this component */
    calculateMargin() {
      if (this.branchElement === undefined) {
        this.marginRight = 0;
        return;
      }
      const rect = this.$refs.wrapper.getBoundingClientRect();
      const parentRect = this.branchElement.getBoundingClientRect();
      this.marginRight = this.isBottom
        ? branchAngleTan * (rect.bottom - parentRect.top)
        : branchAngleTan * (parentRect.bottom - rect.top);
      this.$emit('margin-set', this.marginRight);
    },
    /** Initialize draggable features */
    setupDraggable() {
      $(this.$el).draggable({
        // handle: '.handle',
        revert: isValid => !isValid && this.branchElement,
        distance: 10,
        // this seems to fix droppable 'over' event not fired
        refreshPositions: true,
        start: (e, ui) => {
          $(ui.helper).css('width', `${e.target.offsetWidth}px`);
          this.$emit('drag-started', this);
        },
        helper: (e) => {
          const clone = $(e.currentTarget).clone();
          clone.addClass('ui-draggable-dragging');
          $(this.container).append(clone);
          return clone;
        },
      });
    },
    /** Allow dropping causes on the current one to make it their children */
    setupDroppable() {
      $(this.$el).droppable({
        accept: '.ishikawa-cause',
        tolerance: 'pointer',
        greedy: true,
        drop: (e, ui) => {
          const helper = ui.helper.length ? ui.helper[0] : ui.helper;
          // Droppable has been handled elsewhere
          if (helper.dataset.handled === '1') {
            return;
          }
          // Signal any other droppable handler that this instance took care of it
          helper.dataset.handled = 1;
          const { id } = helper.dataset;
          this.editCause({ cause: this.cause.id, ishikawaBranch: this.cause.ishikawaBranch }, id);
        },
      });
    },
    /** The text changed so the size of the entire element might have changed, thus update margin and canvas */
    textSizeChanged() {
      this.$emit('size-update');
      this.calculateMargin();
      this.drawLinks();
    },
    /** Draw links with the branch and other causes */
    drawLinks() {
      if (!this.branchElement) {
        // Unassigned cause, no links to draw
        return;
      }

      const { canvas, wrapper } = this.$refs;
      const { offsetWidth, offsetHeight } = this.$el;
      const rect = wrapper.getBoundingClientRect();
      const parentRect = this.branchElement.getBoundingClientRect();
      const coreHalfWrapperDistance = this.isBottom
        ? rect.top - parentRect.top + rect.height / 2
        : parentRect.bottom - rect.bottom + rect.height / 2;
      const diagonalXShift = branchAngleTan * (coreHalfWrapperDistance);
      const endXCoordinate = parentRect.right - rect.left - diagonalXShift;
      const halfRectHeight = rect.bottom - rect.height / 2 - this.$el.getBoundingClientRect().top;

      const childCauseEndXCoord = offsetWidth + 10;

      canvas.width = this.isChildCause ? childCauseEndXCoord + 10 : endXCoordinate;
      canvas.height = offsetHeight;
      canvas.style.width = `${this.isChildCause ? childCauseEndXCoord + 10 : endXCoordinate}px`;
      canvas.style.height = `${offsetHeight}px`;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, endXCoordinate, offsetHeight);

      ctx.beginPath();
      if (!this.isChildCause) {
        // Link to the branch line. From the right side of the wrapper to the diagonal line
        ctx.moveTo(offsetWidth, halfRectHeight);
        ctx.lineTo(endXCoordinate, halfRectHeight);
      }
      if (this.cause.wCauses.length) {
        // Links to children
        let yRef = halfRectHeight;
        this.cause.wCauses.forEach((child) => {
          ctx.moveTo(childCauseEndXCoord, yRef);
          const childComponent = this.$refs[`child${child.id}`][0];
          if (childComponent) {
            const { bottom, height } = childComponent.$refs.wrapper.getBoundingClientRect();
            // Set the ref to the center of the child's wrapper element
            yRef = bottom - height / 2 - rect.top;
            ctx.lineTo(childCauseEndXCoord, yRef);
            ctx.lineTo(childCauseEndXCoord - causeMargin - 2 - 5, yRef);
          }
        });
      }
      ctx.lineWidth = 2;
      ctx.strokeStyle = SColors.mainTextColor;
      ctx.stroke();
    },
    /** Add a child cause */
    addCause() {
      this.$store.dispatch(`problem/${ADD_CAUSE}`, { id: this.problemId, cause: this.cause.id })
        .then(() => {
          preventPastingStyles();
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /** CLicking the item triggers the edition of the label */
    startEdit() {
      this.$refs.label.edit();
    },
    /**
     * Handle edit events
     * @param  {Object} params - Params to send through XHR body (id and problemId will be overridden)
     * @param  {Number} causeId - If the cause to edit isn't this.cause, specify the causeId
     */
    editCause(editParams, causeId) {
      causeId = causeId || this.cause.id;
      const params = { id: causeId, problemId: this.problemId, ...editParams };
      this.$store.dispatch(`problem/${EDIT_CAUSE}`, params)
        .then(() => {
          this.sendSocketMessage({ socketCompany: 1, id: this.problemId, msg: SocketMessages.CC_MSGS.PROBLEM_SHEET });
        })
        .catch(this.handleError);
    },
    /** Handle XHR errors */
    handleError(msg) {
      if (msg === -2) {
        calert(this.$t('message.PB_NO_ACCESS'), [{ title: this.$t('message.OK') }]);
      } else {
        calert(this.$t('message.PB_EDITION_ERROR'), [{ title: this.$t('message.OK') }]);
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.ishikawa-cause {
  box-sizing: border-box;
  cursor: pointer;
  position: relative;
  text-align: left;
  // Above other causes when dragging
  z-index: 4;

  $ishi-cause-gen-padding: 0.7rem;
  $ishi-cause-top-padding: 1.5rem;
  $ishi-cause-bot-padding: 0.2rem;

  &.ui-draggable-dragging {
    opacity: 0.7;
  }
  &.in-sheet-selection > .ishikawa-cause__wrapper {
    background: rgba(255, 255, 255, 0.8);
  }
  &__wrapper {
    position: relative;
    padding: $ishi-cause-top-padding $ishi-cause-gen-padding $ishi-cause-bot-padding $ishi-cause-gen-padding;
    border: 1px solid $color-gray-border;
    background: white;
    border-radius: $high-border-radius;

    .dragging-indication {
      position: absolute;
      top: 0px;
      left: 50%;
      transform: translateX(-50%) rotate(90deg) scale(0.8);
    }

    &__text, img {
      display: inline-block;
      vertical-align: top;
    }
    &__text {
      width: calc(100% + .5rem);
      margin-left: 5px;
      background: #fff;
      margin-top: -0.3rem;
      margin-left: -0.5rem;
      border-radius: $main-border-radius;
      box-sizing: border-box;

      &:hover {
        background: $color-gray-background-hover;
      }

      .textarea.generic-border--thin-border.generic-border--focus-shadow {
        box-shadow: none;
        border: none;
        background: transparent;
        cursor: pointer;
        font-weight: 600;
        padding: 0.3rem 0.5rem;
        box-sizing: border-box;
        // 1.3rem + padding
        min-height: 1.9rem;
      }
      .textarea, .editable-label-text {
        padding: 0px;
      }

      &--editing {
        textarea.textarea.generic-border--thin-border.generic-border--focus-shadow {
          cursor: initial;
        }
      }
    }

    &__overlay {
      position: absolute;
      top: 0px;
      left: 0px;
      bottom: 0px;
      right: 0px;
      text-align: right;
    }
  }
  > canvas {
    position: absolute;
    top: 0px;
    left: 0px;
  }

  &__sep {
    height: 1px;
    background: $color-gray-border;
    margin: 0.3rem -0.7rem 0.1rem -0.7rem;
    width: calc(100% + 1.4rem);
  }

  &.ui-droppable-active {
    .ishikawa-cause__wrapper {
      $ishi-drop-padding: calc(#{$ishi-cause-gen-padding} - 2px);
      padding: calc(#{$ishi-cause-top-padding} - 3px) $ishi-drop-padding calc(#{$ishi-cause-bot-padding} - 2px) $ishi-drop-padding;
      border: dashed 3px $color-shizen-blue-btn-shadow-color;

      .dragging-indication {
        top: -2px;
      }
    }
  }

  &.ui-droppable-hover {
    .ishikawa-cause__wrapper {
      background: $color-shizen-blue-btn-shadow-color;
    }
  }

  .actions-per-cause-btn__button {
    margin: 0 -0.5rem;
  }
}
</style>
